/**
 * Quick Add open/closed state (frontend-map §5). UI state only. Bridges the
 * route-aware launchers (the desktop header mic, the nav-menu pill) to the modal
 * that mounts inside the trip route (`QuickAddModal`) — the modal reads `tripId`
 * from its own route params, so this store tracks visibility and launch mode.
 *
 * Voice launches start the microphone HERE, inside the tap handler, because iOS
 * only grants the mic from a direct user gesture; the modal then picks the
 * recorder up from the store.
 *
 * After a commit, `scrollTargetKey` carries the group key (originMasterListId, or
 * 'manual') of the first added item so the board can scroll that list into view.
 */
import { AudioRecorder, describeMicError } from '@/lib/audioRecorder';
import { create } from 'zustand';

export type QuickAddMode = 'voice' | 'text';

interface QuickAddState {
  open: boolean;
  mode: QuickAddMode;
  /** Live microphone capture while a voice launch is listening. */
  recorder: AudioRecorder | null;
  /** Why the mic couldn't start (shown once in the modal, which drops to typing). */
  micError: string | null;
  /** Open listening — call synchronously from a click/tap handler. */
  launchVoice: () => void;
  /** Open with the plain text box. */
  launchText: () => void;
  /** Hand the recorder off (the modal has stopped or cancelled it). */
  releaseRecorder: () => void;
  setOpen: (open: boolean) => void;
  /** Group key to scroll into view after adding, consumed once by the board. */
  scrollTargetKey: string | null;
  setScrollTargetKey: (key: string | null) => void;
}

export const useQuickAddStore = create<QuickAddState>((set, get) => ({
  open: false,
  mode: 'text',
  recorder: null,
  micError: null,
  launchVoice: () => {
    get().recorder?.cancel();
    set({ open: true, mode: 'voice', recorder: null, micError: null });
    AudioRecorder.start().then(
      (recorder) => {
        const state = get();
        if (state.open && state.mode === 'voice' && state.recorder === null) {
          set({ recorder });
        } else {
          recorder.cancel();
        }
      },
      (error: unknown) => {
        if (get().open) {
          set({ mode: 'text', micError: describeMicError(error) });
        }
      },
    );
  },
  launchText: () => {
    get().recorder?.cancel();
    set({ open: true, mode: 'text', recorder: null, micError: null });
  },
  releaseRecorder: () => set({ recorder: null }),
  setOpen: (open) => {
    if (!open) {
      get().recorder?.cancel();
      set({ open: false, recorder: null, micError: null });
      return;
    }
    set({ open: true });
  },
  scrollTargetKey: null,
  setScrollTargetKey: (scrollTargetKey) => set({ scrollTargetKey }),
}));
